import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../Services/Firebase/FirebaseIndex';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../Components/Auth/AuthContext';
import debug from '../utils/debug';

const SecurityNotification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, currentUser } = useAuth();
  
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your request...');
  const [actionType, setActionType] = useState('');
  const [countdown, setCountdown] = useState(5);
  
  const processedRef = useRef(false);
  const countdownRef = useRef(null);
  
  useEffect(() => {
    debug.component.mount('SecurityNotification');
    
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');
    const apiKey = searchParams.get('apiKey');
    
    debug.log('SecurityNotification', 'Processing action', { mode, hasCode: !!oobCode, apiKey });
    
    // Prevent double processing
    if (processedRef.current) {
      debug.log('SecurityNotification', 'Already processed, skipping');
      return;
    }
    processedRef.current = true;
    
    if (!mode || !oobCode) {
      setStatus('error');
      setMessage('Invalid verification link. Please request a new verification email.');
      return;
    }
    
    handleSecurityAction(mode, oobCode);
    
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      debug.component.unmount('SecurityNotification');
    };
  }, [searchParams]);
  
  const handleSecurityAction = async (mode, oobCode) => {
    try {
      switch (mode) {
        case 'verifyEmail':
          await handleEmailVerification(oobCode);
          break;
        case 'resetPassword':
          await handlePasswordReset(oobCode);
          break;
        case 'recoverEmail':
          await handleEmailRecovery(oobCode);
          break;
        default:
          throw new Error(`Unsupported action mode: ${mode}`);
      }
    } catch (error) {
      debug.error('SecurityNotification', 'Error processing action', { mode, error });
      setStatus('error');
      setMessage(getErrorMessage(error));
    }
  };
  
  const handleEmailVerification = async (oobCode) => {
    setActionType('Email Verification');
    setMessage('Verifying your email address...');
    
    try {
      // Store current user info before verification
      const userBeforeVerification = auth.currentUser;
      const userUID = userBeforeVerification?.uid;
      
      debug.log('SecurityNotification', 'Starting verification', { 
        hasCurrentUser: !!userBeforeVerification,
        userUID
      });
      
      // First check if the code is valid
      const actionCodeInfo = await checkActionCode(auth, oobCode);
      debug.log('SecurityNotification', 'Action code verified', { 
        operation: actionCodeInfo.operation,
        email: actionCodeInfo.data?.email
      });
      
      // Apply the verification
      await applyActionCode(auth, oobCode);
      debug.log('SecurityNotification', 'Email verification applied');
      
      // Wait a moment for auth state to stabilize
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if user is still authenticated
      const userAfterVerification = auth.currentUser;
      
      if (!userAfterVerification && userUID) {
        debug.warn('SecurityNotification', 'User logged out during verification, this is expected for some verification flows');
        
        setStatus('success');
        setMessage('🎉 Email verified successfully! Please log in again to continue.');
        
        // Redirect to login with a success message
        setTimeout(() => {
          navigate('/login?verified=true&redirect=' + encodeURIComponent(`/talent-blueprint/${userUID}`), { replace: true });
        }, 3000);
        return;
      }
      
      // If user is still authenticated, reload and refresh
      if (userAfterVerification) {
        await userAfterVerification.reload();
        await refreshUser();
        debug.log('SecurityNotification', 'User reloaded after verification', {
          emailVerified: userAfterVerification.emailVerified
        });
      }
      
      setStatus('success');
      setMessage('🎉 Email verified successfully! Redirecting you to complete your profile...');
      
      // Start countdown and redirect
      startCountdownAndRedirect();
      
    } catch (error) {
      debug.error('SecurityNotification', 'Email verification failed', error);
      throw error;
    }
  };
  
  const handlePasswordReset = async (oobCode) => {
    setActionType('Password Reset');
    
    try {
      // For password reset, we need to redirect to a password reset form
      // with the oobCode as a parameter
      navigate(`/reset-password?oobCode=${oobCode}`, { replace: true });
    } catch (error) {
      debug.error('SecurityNotification', 'Password reset handling failed', error);
      throw error;
    }
  };
  
  const handleEmailRecovery = async (oobCode) => {
    setActionType('Email Recovery');
    setMessage('Processing email recovery request...');
    
    try {
      await applyActionCode(auth, oobCode);
      setStatus('success');
      setMessage('Email recovery completed successfully!');
      startCountdownAndRedirect('/login');
    } catch (error) {
      debug.error('SecurityNotification', 'Email recovery failed', error);
      throw error;
    }
  };
  
  const startCountdownAndRedirect = async (targetPath = null) => {
    // Determine redirect path
    let redirectPath = targetPath;

    if (!redirectPath) {
      // Get user type and redirect to appropriate blueprint page
      const user = auth.currentUser || currentUser;
      if (user) {
        try {
          // Fetch user type from Firestore
          debug.log('SecurityNotification', 'Fetching user type from Firestore', { userUID: user.uid });
          const userDoc = await getDoc(doc(db, 'Users', user.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userType = userData?.profile?.userType || 'Talent'; // Default to Talent

            // Convert to lowercase and create blueprint path
            const blueprintType = userType.toLowerCase() === 'organiser' ? 'organiser' : 'talent';
            redirectPath = `/${blueprintType}-blueprint/${user.uid}`;

            debug.log('SecurityNotification', 'User type found, setting redirect path', {
              userUID: user.uid,
              userType,
              blueprintType,
              redirectPath,
              emailVerified: user.emailVerified
            });
          } else {
            debug.warn('SecurityNotification', 'User document not found in Firestore, defaulting to talent');
            redirectPath = `/talent-blueprint/${user.uid}`;
          }
        } catch (error) {
          debug.error('SecurityNotification', 'Error fetching user type from Firestore', error);
          // Default to talent blueprint if there's an error
          redirectPath = `/talent-blueprint/${user.uid}`;
        }
      } else {
        debug.warn('SecurityNotification', 'No user found, redirecting to login');
        redirectPath = '/login?message=' + encodeURIComponent('Please log in to continue after email verification.');
      }
    }

    debug.log('SecurityNotification', 'Starting countdown redirect', { redirectPath });

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          debug.log('SecurityNotification', 'Redirecting after countdown', { redirectPath });
          navigate(redirectPath, { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const getErrorMessage = (error) => {
    switch (error.code) {
      case 'auth/expired-action-code':
        return 'This verification link has expired. Please request a new verification email.';
      case 'auth/invalid-action-code':
        return 'This verification link is invalid. Please request a new verification email.';
      case 'auth/user-disabled':
        return 'Your account has been disabled. Please contact support.';
      case 'auth/user-not-found':
        return 'User account not found. Please register a new account.';
      default:
        return error.message || 'An error occurred while processing your request. Please try again.';
    }
  };
  
  const handleManualRedirect = async () => {
    if (status === 'success') {
      const user = auth.currentUser || currentUser;
      if (user) {
        try {
          // Fetch user type from Firestore
          const userDoc = await getDoc(doc(db, 'Users', user.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userType = userData?.profile?.userType || 'Talent';
            const blueprintType = userType.toLowerCase() === 'organiser' ? 'organiser' : 'talent';
            const redirectPath = `/${blueprintType}-blueprint/${user.uid}`;

            debug.log('SecurityNotification', 'Manual redirect with user type', {
              userUID: user.uid,
              userType,
              blueprintType,
              redirectPath
            });

            navigate(redirectPath, { replace: true });
          } else {
            // Default to talent if no user doc found
            navigate(`/talent-blueprint/${user.uid}`, { replace: true });
          }
        } catch (error) {
          debug.error('SecurityNotification', 'Error in manual redirect', error);
          // Default to talent blueprint if there's an error
          navigate(`/talent-blueprint/${user.uid}`, { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    } else {
      navigate('/login', { replace: true });
    }
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#002244',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Montserrat, sans-serif',
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '15px',
        padding: '3rem',
        width: '100%',
        maxWidth: '600px',
        textAlign: 'center',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Logo */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            color: '#01a2f1',
            fontFamily: 'Pondar, sans-serif',
            fontSize: '2.5rem',
            margin: 0
          }}>
            GIGiFY
          </h1>
        </div>
        
        {/* Status Icon */}
        <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>
          {status === 'processing' && '⏳'}
          {status === 'success' && '✅'}
          {status === 'error' && '❌'}
        </div>
        
        {/* Title */}
        <h2 style={{
          color: '#144874',
          fontSize: '1.8rem',
          marginBottom: '1rem',
          fontWeight: '600'
        }}>
          {actionType || 'Security Notification'}
        </h2>
        
        {/* Message */}
        <p style={{
          color: '#144874',
          fontSize: '1.1rem',
          lineHeight: '1.6',
          marginBottom: '2rem'
        }}>
          {message}
        </p>
        
        {/* Countdown for success */}
        {status === 'success' && countdown > 0 && (
          <p style={{
            color: '#01a2f1',
            fontSize: '1rem',
            marginBottom: '1.5rem'
          }}>
            Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
          </p>
        )}
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {status === 'success' && (
            <button
              onClick={handleManualRedirect}
              style={{
                backgroundColor: '#01a2f1',
                color: '#fff',
                padding: '0.75rem 2rem',
                border: 'none',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '150px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0186c7';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#01a2f1';
                e.target.style.transform = 'scale(1)';
              }}
            >
              Continue Now
            </button>
          )}
          
          {status === 'error' && (
            <>
              <button
                onClick={() => navigate('/email-verification?resend=true', { replace: true })}
                style={{
                  backgroundColor: '#f85008',
                  color: '#fff',
                  padding: '0.75rem 2rem',
                  border: 'none',
                  borderRadius: '25px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '150px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e04307';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f85008';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                Resend Email
              </button>
              
              <button
                onClick={() => navigate('/login', { replace: true })}
                style={{
                  backgroundColor: 'transparent',
                  color: '#144874',
                  padding: '0.75rem 2rem',
                  border: '2px solid #144874',
                  borderRadius: '25px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '150px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#144874';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#144874';
                }}
              >
                Back to Login
              </button>
            </>
          )}
          
          {status === 'processing' && (
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(1, 162, 241, 0.2)',
              borderTop: '4px solid #01a2f1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          )}
        </div>
        
        {/* Help Text */}
        <p style={{
          marginTop: '2rem',
          fontSize: '0.9rem',
          color: '#666',
          fontStyle: 'italic'
        }}>
          Having trouble? Contact our support team at{' '}
          <a href="mailto:support@gigify.co.za" style={{ color: '#01a2f1' }}>
            support@gigify.co.za
          </a>
        </p>
        
        {/* CSS Animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default SecurityNotification;